import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Alert, AlertDescription } from "@components/ui/alert";
import { requestPasswordReset } from '../api/reset-password';
import type { PasswordResetRequestFormData } from '../types';

export const PasswordResetRequestForm: React.FC = () => {
  const [formData, setFormData] = useState<PasswordResetRequestFormData>({
    email: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData({ email: value });
    if (error) {
      setError('');
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await requestPasswordReset({ email: formData.email });
      setIsSuccess(true);
      setSuccessMessage(response.message);
    } catch (error: any) {
      console.error('Password reset request error:', error);
      setError(
        error?.data?.error ||
        error?.message ||
        'An error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message
  if (isSuccess) {
    return (
      <div className="space-y-4">
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold mb-1">Check Your Email</p>
                <p className="text-sm">{successMessage}</p>
              </div>
            </div>
          </AlertDescription>
        </Alert>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900 font-semibold mb-2">
            📧 Next Steps
          </p>
          <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
            <li>Check your email inbox for a password reset link</li>
            <li>Click the link to create a new password</li>
            <li>Log in with your new password</li>
          </ol>
        </div>

        <div className="text-sm text-gray-600">
          <p className="mb-1">Didn't receive the email?</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Check your spam or junk folder</li>
            <li>Make sure you entered the correct email address</li>
            <li>Wait a few minutes and check again</li>
          </ul>
        </div>

        <div className="text-center text-sm">
          <Link to="/login" className="text-primary-500 hover:text-primary-600 underline">
            ← Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Email Address <span className="text-red-500">*</span>
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="your.email@company.com"
          required
          value={formData.email}
          onChange={handleInputChange}
          className={error ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {error && (
          <p className="text-sm text-red-600 font-medium">{error}</p>
        )}
      </div>

      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Sending..." : "Send Reset Link"}
      </Button>

      <div className="text-center text-sm text-gray-600">
        Remember your password?{' '}
        <Link to="/login" className="text-primary-500 hover:text-primary-600 underline">
          Back to Login
        </Link>
      </div>
    </form>
  );
};

